/* eslint-disable import/namespace */
/* eslint-disable react/no-deprecated */

import components from './components/index.js'
import { createElement } from 'react'
import { fromJSON, getUniqueId } from './utils.js'

/**
 * @type {{ (container: any, reactElement: any): void; } | null}
 */
let hydrate = null
async function getHydrateComponent() {
  if (typeof hydrate === 'function') {
    return hydrate
  }
  try {
    const internalHydrate = (await import('react-dom/client')).hydrateRoot
    hydrate = (container, reactElement) => {
      internalHydrate(container, reactElement)
    }
  } catch (error) {
    console.error('Error importing react-dom/client', error)
    const internalHydrate = (await import('react-dom')).hydrate
    hydrate = (container, reactElement) => {
      internalHydrate(reactElement, container)
    }
  }
  return hydrate
}

/**
 * @param {HTMLElement} container
 */
function getProps(container) {
  try {
    if (!container.dataset.json) return {}
    const props = fromJSON(container.dataset.json) || {}
    return props
  } catch (error) {
    console.error('Shared component error: Error parsing props', error)
    return {}
  }
}

// eslint-disable-next-line import/prefer-default-export
/**
 * @param {React.ComponentType<any>} Component
 * @param {{ (container: any, reactElement: any): void; }} hydrate
 * @param {string} key
 * @returns {Promise<() => boolean>}
 */
async function getClientRenderFn(Component, hydrate, key) {
  const uniqueId = await getUniqueId(Component, key)
  return function render() {
    const container = document.getElementById(uniqueId)
    if (!container) {
      return false
    }
    const props = getProps(container)

    hydrate?.(container, createElement(Component, props))
    if (container.children.length > 1) {
      console.error('Shared component error: Error rendering component', Component, props)
      container.lastChild?.remove()
    }
    return true
  }
}

async function hydrateComponents() {
  const hydrate = await getHydrateComponent()
  const hydrated = new Set()
  // eslint-disable-next-line no-unused-vars
  async function inner() {
    const promises = Object.entries(components).map(async ([key, component]) => {
      if (hydrated.has(key)) return
      const render = await getClientRenderFn(component, hydrate, key)
      const result = render()
      if (result) {
        hydrated.add(key)
      }
    })
    await Promise.all(promises)
  }
  await inner()
  // repeat hydration on DOMContentLoaded -> this is necessary for static pages
  document.addEventListener('DOMContentLoaded', inner)
}

hydrateComponents().then().catch(console.error)
